import type { User } from '@/types/user';

const PAUSE_BLOCKED_PATHS = ['/videos', '/series', '/community', '/shorts', '/video-player'];

/**
 * Routes a paused-and-expired member cannot reach. Surfaces the PauseGate
 * full-screen modal when matched. Everything else (home, profile, creator
 * pages, account settings, support, legal, studio for creators) stays
 * reachable so the member can manage their subscription and browse public
 * surfaces without hitting a wall.
 */
export function isPauseBlockedRoute(pathname: string): boolean {
  return PAUSE_BLOCKED_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

const ADMIN_EMAIL = process.env.NEXT_PUBLIC_ADMIN_EMAIL;

/** Whether the user is the platform admin (tech account). */
function isAdminEmail(user: User | null | undefined): boolean {
  return !!ADMIN_EMAIL && user?.email === ADMIN_EMAIL;
}

// TEMP — creator-test gate for the Travel Knowledge / Travel Tips sidebar
// entries (FRONT-181). Visible only to the allow-listed testers, hidden for
// everyone else, until the feature ships to all creators. On full release,
// delete this list + helper and the two sidebar call-sites. The env var
// overrides the baked-in default so testers can be swapped without a code edit.
const TRAVEL_KNOWLEDGE_EMAILS = (
  process.env.NEXT_PUBLIC_TRAVEL_KNOWLEDGE_EMAILS ??
  'tech@taboo.tv,arab@taboo.tv,igortech@taboo.tv,rami@taboo.tv'
)
  .split(',')
  .map((email) => email.trim().toLowerCase())
  .filter(Boolean);

/** Whether the Travel Knowledge / Travel Tips sidebar entries should show. */
export function canSeeTravelKnowledge(user: User | null | undefined): boolean {
  const email = user?.email?.toLowerCase();
  return !!email && TRAVEL_KNOWLEDGE_EMAILS.includes(email);
}

// TEMP — Discord AI-beta opt-in (visibility only, not security: quota/402 are
// enforced BE-side on /answer). Set by `AiBetaProvider` when a page is opened
// with `?ai_beta=1` (cleared by `?ai_beta=0`). Read per call, not cached, so
// the kill-switch takes effect without a reload. Delete at GA together with
// the provider, this helper, and the allowlist above.
export const AI_BETA_STORAGE_KEY = 'tabootv-ai-beta';

function hasAiBetaOptIn(): boolean {
  if (globalThis.window === undefined) return false;
  try {
    return globalThis.localStorage.getItem(AI_BETA_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Whether the Taboo Terra entries (header orb, watch-page Ask, search Ask row)
 * should show. Two beta doors, either opens it:
 * - the Travel Knowledge tester allowlist (same env var, no duplicate flag), or
 * - a Discord-linked member (`has_discord_access`, BE-owned) who opted in via
 *   the `?ai_beta=1` link shared in the Discord server.
 */
export function canSeeTabooAi(user: User | null | undefined): boolean {
  return canSeeTravelKnowledge(user) || (!!user?.has_discord_access && hasAiBetaOptIn());
}

// TEMP — Paid DMs / Taboo Coins tester gate (FRONT-307/308). Visibility only,
// not security: the DM/coins endpoints are auth-gated BE-side and spend safety
// is server-owned. Reuses the tester allowlist (same env var, no duplicate
// flag) so tech@ / arab@ see the surfaces while everyone else doesn't. On full
// release, delete this helper and its call sites (query-hook `enabled` gates +
// surface entry points).
/** Whether the Paid DMs + Taboo Coins surfaces (wallet, inbox, entry points) should show. */
export function canSeePaidDms(user: User | null | undefined): boolean {
  return canSeeTravelKnowledge(user);
}

// TEMP — Import-from-URL tester gate (FRONT-327). The "Import from a link"
// tab in the studio UploadModal stays tester-only until the funnel
// (studio_url_fetch_*, FRONT-341) shows the BE share-link resolver holding up
// in the wild. Visibility only, not security: the
// /studio/videos/fetch-from-url endpoint stays auth-gated BE-side. Reuses the
// tester allowlist (same env var, no duplicate flag). On full release, delete
// this helper and its call site.
/** Whether the studio UploadModal shows the "Import from a link" tab. */
export function canSeeUrlImport(user: User | null | undefined): boolean {
  return canSeeTravelKnowledge(user);
}

/** Creator whose channel has not yet been published (private channel). */
export function isPrivateCreator(user: User | null | undefined): boolean {
  return !!user?.is_creator && (!user.channel || user.channel.published_at === null);
}

/** Creator with a published (public) channel. */
export function isCreatorWithChannel(user: User | null | undefined): boolean {
  return !!user?.is_creator && !!user.channel && user.channel.published_at !== null;
}

/**
 * Only creators with an active channel can compose community posts.
 * Private channel creators are blocked.
 */
export function canPostInCommunity(user: User | null | undefined): boolean {
  return isCreatorWithChannel(user) || isAdminEmail(user);
}

/** Any creator (private or not) can upload video/short content. */
export function canUploadContent(user: User | null | undefined): boolean {
  return !!user?.is_creator;
}

/** Whether the creator has signed the Content Creation Agreement. */
export function hasSignedCreatorContract(user: User | null | undefined): boolean {
  return !!user?.creator_contract_date;
}

/**
 * Strict gate for any creator-only mutation (upload, post, schedule, codes).
 * Combines creator status with signed agreement — UI may still surface entry
 * points to unsigned creators, but the action itself must route through the
 * contract dialog.
 */
export function canPerformCreatorActions(user: User | null | undefined): boolean {
  return canUploadContent(user) && hasSignedCreatorContract(user);
}
